/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Badge,
  Paper,
  Skeleton
} from '@material-ui/core';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Link as LinkIcon,
  Award,
  Clock,
  Moon,
  Sun,
  Bell,
  BarChart2,
  Activity
} from 'react-feather';
import { makeStyles, ThemeProvider } from '@material-ui/styles';
import { createMuiTheme } from '@material-ui/core/styles';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import affiliateService from '../services/affiliateService';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b'];

const DashboardAfiliados = () => {
  const auth = useSelector((state) => state.auth);
  const [darkMode, setDarkMode] = useState(false);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState(3);
  const [stats, setStats] = useState({
    totalCommission: 0,
    averageCommission: 0,
    totalLinks: 0,
    activeLinks: 0,
    totalChannels: 0,
    approvedChannels: 0,
    commissions: {
      pending_payment: { count: 0, total: 0 },
      pending_release: { count: 0, total: 0 },
      released: { count: 0, total: 0 },
      paid: { count: 0, total: 0 },
      cancelled: { count: 0, total: 0 }
    },
    timeline: [],
    byChannel: [],
    availableCommission: 0,
    trend: 15.3,
    previousPeriod: {
      commission: 0
    }
  });

  // Paleta fixa por plataforma conhecida (fallback pra cor genérica)
  const PLATFORM_COLORS = {
    instagram: '#E1306C',
    tiktok: '#000000',
    youtube: '#FF0000',
    facebook: '#1877F2',
    twitter: '#1DA1F2',
    x: '#1DA1F2',
    whatsapp: '#25D366',
    telegram: '#0088CC',
    blog: '#FF6600',
    site: '#667eea'
  };

  const resolvePlatformColor = (platform, fallback = '#667eea') => {
    if (!platform) return fallback;
    return PLATFORM_COLORS[String(platform).toLowerCase()] || fallback;
  };

  // Evolução de comissões por mês
  const commissionTrendData = (stats.timeline || []).map((row) => ({
    name: row.label,
    comissao: Number((row.commission_amount || 0).toFixed(2))
  }));

  // Performance por canal — usa soma de comissões
  const channelPerformanceData = (stats.byChannel || []).slice(0, 8).map((row, idx) => ({
    name: row.channel_name,
    value: Number((row.commission_amount || 0).toFixed(2)),
    count: row.sales_count,
    color: resolvePlatformColor(row.platform, COLORS[idx % COLORS.length])
  }));

  // Comissão média por mês — derivado do timeline
  const averageCommissionData = (stats.timeline || []).map((row) => ({
    name: row.label,
    media: Number((row.average_commission || 0).toFixed(2))
  }));

  const renderEmptyState = (message) => (
    <Box sx={{ py: 8, textAlign: 'center' }}>
      <Typography variant="body2" color="textSecondary">
        {message}
      </Typography>
    </Box>
  );

  const commissionsPieData = [
    { name: 'Pendente Pagamento', value: stats.commissions.pending_payment.count || 1 },
    { name: 'A Liberar', value: stats.commissions.pending_release.count || 1 },
    { name: 'Liberado', value: stats.commissions.released.count || 1 },
    { name: 'Pago', value: stats.commissions.paid.count || 1 },
    { name: 'Cancelado', value: stats.commissions.cancelled.count || 1 }
  ];

  const theme = createMuiTheme({
    palette: {
      type: darkMode ? 'dark' : 'light',
      primary: { main: '#667eea' },
      secondary: { main: '#764ba2' },
      background: {
        default: darkMode ? '#0a0e27' : '#f5f7fa',
        paper: darkMode ? '#151b35' : '#ffffff'
      }
    }
  });

  const useStyles = makeStyles(() => ({
    root: {
      background: darkMode
        ? 'linear-gradient(135deg, #0a0e27 0%, #151b35 50%, #1a1f3a 100%)'
        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    card: {
      background: darkMode
        ? 'rgba(21, 27, 53, 0.8)'
        : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px) saturate(180%)',
      borderRadius: 24,
      boxShadow: darkMode
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
        : '0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
      border: darkMode
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(255, 255, 255, 0.3)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-10px) scale(1.02)',
        boxShadow: darkMode
          ? '0 20px 60px 0 rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
          : '0 20px 60px 0 rgba(31, 38, 135, 0.3), inset 0 1px 0 rgba(255, 255, 255, 1)'
      }
    },
    metricCard: {
      color: '#fff',
      borderRadius: 24,
      boxShadow: '0 15px 50px 0 rgba(102, 126, 234, 0.35)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.15), transparent)',
        opacity: 0,
        transition: 'opacity 0.4s'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.2), transparent)',
        top: -150,
        right: -150,
        animation: '$pulse 4s ease-in-out infinite'
      },
      '&:hover': {
        transform: 'translateY(-12px) scale(1.05) rotateZ(1deg)',
        boxShadow: '0 25px 70px 0 rgba(102, 126, 234, 0.5)',
        '&::before': {
          opacity: 1
        }
      }
    },
    '@keyframes pulse': {
      '0%, 100%': {
        transform: 'scale(1)',
        opacity: 0.5
      },
      '50%': {
        transform: 'scale(1.1)',
        opacity: 0.8
      }
    },
    avatar: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      height: 72,
      width: 72,
      backdropFilter: 'blur(10px)',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
    },
    trendChip: {
      background: 'rgba(255, 255, 255, 0.25)',
      color: '#fff',
      fontWeight: 700,
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    sparkline: {
      position: 'absolute',
      bottom: 0,
      right: 0,
      opacity: 0.25,
      pointerEvents: 'none'
    },
    headerCard: {
      background: darkMode
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: 24,
      padding: theme.spacing(3),
      boxShadow: '0 15px 50px 0 rgba(102, 126, 234, 0.35)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.15), transparent)',
        top: -200,
        right: -200
      }
    }
  }));

  const MetricCard = ({ gradient, icon: Icon, title, value, subtitle, trend, comparison, sparklineData }) => {
    const classes = useStyles();

    return (
      <Card
        className={classes.metricCard}
        style={{ background: gradient }}
      >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Avatar className={classes.avatar}>
              <Icon size={32} />
            </Avatar>
            {trend !== undefined && (
              <Chip
                icon={trend > 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                label={`${trend > 0 ? '+' : ''}${trend}%`}
                className={classes.trendChip}
                size="small"
              />
            )}
          </Box>
          <Typography variant="overline" sx={{ opacity: 0.9, fontSize: 12, fontWeight: 600, letterSpacing: 1.5 }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
              {subtitle}
            </Typography>
          )}
          {comparison && (
            <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
              vs período anterior: {comparison}
            </Typography>
          )}
          {sparklineData && (
            <Box className={classes.sparkline}>
              <ResponsiveContainer width={140} height={50}>
                <AreaChart data={sparklineData}>
                  <Area
                    type="monotone"
                    dataKey="comissao"
                    stroke="#fff"
                    fill="rgba(255,255,255,0.3)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  MetricCard.propTypes = {
    gradient: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    subtitle: PropTypes.string,
    trend: PropTypes.number,
    comparison: PropTypes.string,
    sparklineData: PropTypes.arrayOf(PropTypes.object)
  };

  MetricCard.defaultProps = {
    subtitle: null,
    trend: null,
    comparison: null,
    sparklineData: null
  };

  const SkeletonCard = () => {
    const classes = useStyles();
    return (
      <Card className={classes.card}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: 2 }} />
          </Box>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" />
        </CardContent>
      </Card>
    );
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [
        salesRes,
        linksRes,
        channelsRes,
        commissionsRes,
        availableRes,
        timelineRes,
        byChannelRes
      ] = await Promise.all([
        affiliateService.getSalesStats(auth.token),
        affiliateService.getAffiliateLinks(auth.token, { per_page: 1 }),
        affiliateService.getChannels(auth.token, { per_page: 1 }),
        affiliateService.getCommissionsStats(auth.token),
        affiliateService.getAvailableCommission(auth.token),
        affiliateService.getSalesTimeline(auth.token, { months: 6 }),
        affiliateService.getSalesByChannel(auth.token)
      ]);

      const totalCommission = salesRes?.data?.total_commission || 0;

      setStats({
        totalCommission,
        averageCommission: salesRes?.data?.average_commission || 0,
        totalLinks: linksRes?.data?.total || 0,
        activeLinks: linksRes?.data?.data?.filter((l) => l.active)?.length || 0,
        totalChannels: channelsRes?.data?.total || 0,
        approvedChannels: channelsRes?.data?.data?.filter((c) => c.approved)?.length || 0,
        commissions: commissionsRes?.data || {
          pending_payment: { count: 0, total: 0 },
          pending_release: { count: 0, total: 0 },
          released: { count: 0, total: 0 },
          paid: { count: 0, total: 0 },
          cancelled: { count: 0, total: 0 }
        },
        timeline: Array.isArray(timelineRes?.data) ? timelineRes.data : [],
        byChannel: Array.isArray(byChannelRes?.data) ? byChannelRes.data : [],
        availableCommission: availableRes?.data?.available || 0,
        trend: 15.3,
        previousPeriod: {
          commission: totalCommission ? totalCommission * 0.85 : 0
        }
      });
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setTimeout(() => setLoading(false), 800);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const classes = useStyles();

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const calculateGrowth = (current, previous) => {
    if (!previous) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>Dashboard | Sistema de Afiliados Diva</title>
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth="xl">
          {/* Header */}
          <Paper
            className={classes.headerCard}
            sx={{ mb: 4, position: 'relative', color: '#fff' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                  👋 Olá, {auth.user?.name?.split(' ')[0]}!
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Acompanhe suas métricas e performance em tempo real
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    color: '#fff',
                    fontWeight: 600,
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid rgba(255,255,255,0.3)'
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#fff'
                    }
                  }}
                >
                  <MenuItem value="7">Últimos 7 dias</MenuItem>
                  <MenuItem value="30">Últimos 30 dias</MenuItem>
                  <MenuItem value="90">Últimos 90 dias</MenuItem>
                  <MenuItem value="365">Último ano</MenuItem>
                </Select>
                <Tooltip title="Notificações">
                  <IconButton sx={{ color: '#fff' }}>
                    <Badge badgeContent={notifications} color="error">
                      <Bell />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Tooltip title={darkMode ? 'Modo Claro' : 'Modo Escuro'}>
                  <IconButton onClick={() => setDarkMode(!darkMode)} sx={{ color: '#fff' }}>
                    {darkMode ? <Sun /> : <Moon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Cards de Métricas */}
            {loading ? (
              <>
                <Grid item xs={12} sm={6} md={3}><SkeletonCard /></Grid>
                <Grid item xs={12} sm={6} md={3}><SkeletonCard /></Grid>
                <Grid item xs={12} sm={6} md={3}><SkeletonCard /></Grid>
                <Grid item xs={12} sm={6} md={3}><SkeletonCard /></Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
                    icon={DollarSign}
                    title="Comissão Total"
                    value={formatCurrency(stats.totalCommission)}
                    subtitle={`Média: ${formatCurrency(stats.averageCommission)} / venda`}
                    trend={Number(calculateGrowth(stats.totalCommission, stats.previousPeriod.commission))}
                    comparison={formatCurrency(stats.previousPeriod.commission)}
                    sparklineData={commissionTrendData}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                    icon={Clock}
                    title="A Receber"
                    value={formatCurrency(
                      (stats.commissions.pending_payment?.total || 0)
                      + (stats.commissions.pending_release?.total || 0)
                      + (stats.commissions.released?.total || 0)
                    )}
                    subtitle="Aguardando liberação/pagamento"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                    icon={LinkIcon}
                    title="Links Ativos"
                    value={`${stats.activeLinks}/${stats.totalLinks}`}
                    subtitle={`${stats.totalLinks > 0 ? ((stats.activeLinks / stats.totalLinks) * 100).toFixed(0) : 0}% taxa de ativação`}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    icon={Award}
                    title="Disponível"
                    value={formatCurrency(stats.availableCommission)}
                    subtitle="Pronto para saque"
                  />
                </Grid>
              </>
            )}

            {/* Gráfico de Evolução de Comissões */}
            <Grid item xs={12} md={8}>
              <Card className={classes.card}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={24} style={{ color: '#667eea' }} />
                        Evolução de Comissões
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Comissões acumuladas nos últimos 6 meses
                      </Typography>
                    </Box>
                  </Box>
                  {loading && <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />}
                  {!loading && commissionTrendData.length === 0 && renderEmptyState('Sem comissões no período.')}
                  {!loading && commissionTrendData.length > 0 && (
                    <ResponsiveContainer width="100%" height={320}>
                      <AreaChart data={commissionTrendData}>
                        <defs>
                          <linearGradient id="colorComissao" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#e0e0e0'} />
                        <XAxis dataKey="name" stroke={darkMode ? '#fff' : '#666'} />
                        <YAxis
                          stroke={darkMode ? '#fff' : '#666'}
                          tickFormatter={(v) => formatCurrency(v)}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            background: darkMode ? '#1a1f3a' : '#fff',
                            border: `1px solid ${darkMode ? '#333' : '#e0e0e0'}`,
                            borderRadius: 12
                          }}
                          formatter={(value) => [formatCurrency(value), 'Comissão']}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="comissao"
                          name="Comissão"
                          stroke="#667eea"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#colorComissao)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Distribuição de Comissões */}
            <Grid item xs={12} md={4}>
              <Card className={classes.card}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DollarSign size={24} style={{ color: '#4facfe' }} />
                    Comissões
                  </Typography>
                  {loading ? (
                    <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto' }} />
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={commissionsPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={100}
                          dataKey="value"
                          label={(entry) => entry.name}
                        >
                          {commissionsPieData.map((entry) => (
                            <Cell key={entry.name} fill={COLORS[commissionsPieData.indexOf(entry) % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                  <Box sx={{ mt: 2 }}>
                    {[
                      { label: 'Pendente Pagamento', value: formatCurrency(stats.commissions.pending_payment.total), color: COLORS[0] },
                      { label: 'A Liberar', value: formatCurrency(stats.commissions.pending_release.total), color: COLORS[1] },
                      { label: 'Liberado', value: formatCurrency(stats.commissions.released.total), color: COLORS[2] },
                      { label: 'Pago', value: formatCurrency(stats.commissions.paid.total), color: COLORS[3] },
                      { label: 'Cancelado', value: formatCurrency(stats.commissions.cancelled.total), color: COLORS[4] }
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: item.label !== 'Rejeitadas' ? `1px solid ${darkMode ? '#333' : '#f0f0f0'}` : 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: item.color
                            }}
                          />
                          <Typography variant="body2">{item.label}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Comissões por Canal */}
            <Grid item xs={12} md={6}>
              <Card className={classes.card}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart2 size={24} style={{ color: '#43e97b' }} />
                    Comissões por Canal
                  </Typography>
                  {loading && <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />}
                  {!loading && channelPerformanceData.length === 0 && renderEmptyState('Nenhum canal com comissões ainda.')}
                  {!loading && channelPerformanceData.length > 0 && (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={channelPerformanceData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#e0e0e0'} />
                        <XAxis type="number" stroke={darkMode ? '#fff' : '#666'} tickFormatter={(v) => formatCurrency(v)} />
                        <YAxis dataKey="name" type="category" stroke={darkMode ? '#fff' : '#666'} width={120} />
                        <RechartsTooltip
                          contentStyle={{
                            background: darkMode ? '#1a1f3a' : '#fff',
                            border: `1px solid ${darkMode ? '#333' : '#e0e0e0'}`,
                            borderRadius: 12
                          }}
                          formatter={(value) => [formatCurrency(value), 'Comissão']}
                        />
                        <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                          {channelPerformanceData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Comissão Média */}
            <Grid item xs={12} md={6}>
              <Card className={classes.card}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp size={24} style={{ color: '#fa709a' }} />
                    Comissão Média por Mês
                  </Typography>
                  {loading && <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />}
                  {!loading && averageCommissionData.length === 0 && renderEmptyState('Sem dados no período.')}
                  {!loading && averageCommissionData.length > 0 && (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={averageCommissionData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#e0e0e0'} />
                        <XAxis dataKey="name" stroke={darkMode ? '#fff' : '#666'} />
                        <YAxis
                          stroke={darkMode ? '#fff' : '#666'}
                          tickFormatter={(v) => formatCurrency(v)}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            background: darkMode ? '#1a1f3a' : '#fff',
                            border: `1px solid ${darkMode ? '#333' : '#e0e0e0'}`,
                            borderRadius: 12
                          }}
                          formatter={(value) => [formatCurrency(value), 'Comissão Média']}
                        />
                        <Line
                          type="monotone"
                          dataKey="media"
                          stroke="#fa709a"
                          strokeWidth={4}
                          dot={{ fill: '#fa709a', r: 6 }}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardAfiliados;
